import { useState } from 'react';

import useAuthenticationStatus from 'legacy/common/hooks/useAuthenticationStatus';
import { logEvent } from 'legacy/common/utils/amplitude';
import { EVENT_REDIRECT_ON_CHECKOUT_SESSION_COMPLETED_ERROR } from 'legacy/common/constants/events';
import useParams from 'legacy/common/hooks/useParams';
import { DiscountCodeDto, EventForUser } from 'legacy/events/types';
import logTransactionToGA from '../helpers/logTransactionToGA';
import {
  useStartMultiTierCheckoutSession,
  useUpdateMultiTierCheckoutSession,
  useConfirmMultiTierCheckoutSession,
  useStartUnauthenticatedMultiTierCheckoutSession,
  useUpdateUnauthenticatedMultiTierCheckoutSession
} from 'api/queries/events/checkout';
import usePaymentMethod from 'hooks/usePaymentMethod';
import computeCheckoutSessionUpdate from '../helpers/computeCheckoutSessionUpdate';
import { ConfirmCheckoutSessionParams } from 'api/types/events/checkout';
import { EventProductMappedToTier } from 'legacy/common/types/models';
import { MultiTierCheckoutSession, UpdateCheckoutFn } from '../../types';
import { shouldDisableCheckoutForError } from '../../helpers/checkoutErrorHelpers';

const useMultiTierCheckoutSession = (
  event: EventForUser,
  discountData: DiscountCodeDto,
  ticketTiers: EventProductMappedToTier[],
  isCheckoutExpired: boolean
) => {
  const params = useParams<{ t?: string }>();
  const { isAuthenticated } = useAuthenticationStatus();
  const { getCardStripePaymentMethodIdFromCardId } = usePaymentMethod();
  const [checkoutSession, setCheckoutSession] =
    useState<MultiTierCheckoutSession>();

  const startCheckoutSession = useStartMultiTierCheckoutSession();
  const startUnauthenticatedCheckoutSession =
    useStartUnauthenticatedMultiTierCheckoutSession();
  const updateCheckoutSession = useUpdateMultiTierCheckoutSession();
  const updateUnauthenticatedCheckoutSession =
    useUpdateUnauthenticatedMultiTierCheckoutSession();
  const confirmCheckoutSession = useConfirmMultiTierCheckoutSession();

  /**
   * Starts the checkout process for a given event.
   */
  const startCheckout = (eventId: string) => {
    if (!eventId) {
      return;
    }

    const startCheckoutMutation = isAuthenticated()
      ? startCheckoutSession
      : startUnauthenticatedCheckoutSession;

    startCheckoutMutation.mutate(eventId, {
      onSuccess: (data) => {
        setCheckoutSession(data as MultiTierCheckoutSession);
      }
    });
  };

  /**
   * Update the checkout session with given parameters and options.
   */
  const updateCheckout: UpdateCheckoutFn = (updateData, cards) =>
    new Promise((resolve, reject) => {
      if (!checkoutSession?.paymentIntentId || !event?._id) {
        reject(
          new Error('paymentIntentId not provided or event id not provided')
        );
      }

      const isUserAuthenticated = isAuthenticated();

      const updateCheckoutMutation = isUserAuthenticated
        ? updateCheckoutSession
        : updateUnauthenticatedCheckoutSession;

      startCheckoutSession.reset();
      confirmCheckoutSession.reset();

      updateCheckoutMutation.mutate(
        {
          eventId: event._id,
          paymentIntentId: checkoutSession?.paymentIntentId,
          body: computeCheckoutSessionUpdate(
            {
              ...updateData,
              promoterCode: updateData.promoterCode,
              trackingId: params.t,
              stripePaymentMethodType: updateData.stripePaymentMethodType,
              stripePaymentMethodId: getCardStripePaymentMethodIdFromCardId(
                updateData.card,
                cards
              )
            },
            isUserAuthenticated
          )
        },
        {
          onSuccess: (data) => {
            const newCheckoutSessionData = {
              ...data,
              card: updateData.card
            };

            setCheckoutSession(newCheckoutSessionData);
            resolve(newCheckoutSessionData);
          },
          onError: (error) => {
            reject(error);
          }
        }
      );
    });

  /**
   * Confirms the checkout session with the provided data.
   * Also logs the event in Google Analytics if transaction was successful.
   */
  const confirmCheckout = (confirmData: { allWaiversAccepted: boolean }) =>
    new Promise((resolve, reject) => {
      if (!checkoutSession?.paymentIntentId || !event?._id) {
        reject(
          new Error('paymentIntentId not provided or event id not provided')
        );
      }

      startCheckoutSession.reset();
      updateCheckoutSession.reset();

      const mutationParams = {
        eventId: event._id,
        paymentIntentId: checkoutSession.paymentIntentId
      } as ConfirmCheckoutSessionParams;

      mutationParams.body = {
        isWaiverAccepted: confirmData?.allWaiversAccepted
      };

      confirmCheckoutSession.mutate(mutationParams, {
        onSuccess: (data) => {
          // Add transaction to google analytics
          logTransactionToGA(checkoutSession, event, discountData, ticketTiers);

          if (data?.redirectUrl) {
            // Write the checkout data to local storage to be used in the StripeStatusBanner post checkout
            localStorage.setItem(
              'bounceCurrentCheckoutData',
              JSON.stringify({
                eventId: event._id,
                eventName: event.title,
                numberOfTickets: Object.values(
                  checkoutSession.ticketBasket
                ).reduce((acc, curr) => acc + curr, 0),
                value: checkoutSession.total || 0,
                currency: checkoutSession.currency || 'USD',
                metaPixelId: event.metaPixelId
              })
            );

            // Create an empty document to prevent any unsaved changes dialogs
            document.open();
            document.write(
              `<html><head><meta http-equiv="refresh" content="0; url=${data.redirectUrl}"></head><body>Redirecting...</body></html>`
            );
            document.close();
          } else {
            resolve(null);
          }
        },
        onError: (err) => {
          if (err.code === 'checkoutSessionCompleted') {
            logEvent(EVENT_REDIRECT_ON_CHECKOUT_SESSION_COMPLETED_ERROR);
            return resolve(null);
          }

          return reject(err);
        }
      });
    });

  return {
    checkoutSession,
    isLoadingCheckout:
      startCheckoutSession.isLoading ||
      startUnauthenticatedCheckoutSession.isLoading ||
      updateCheckoutSession.isLoading ||
      updateUnauthenticatedCheckoutSession.isLoading ||
      confirmCheckoutSession.isLoading,
    isStartingCheckout:
      startCheckoutSession.isLoading ||
      startUnauthenticatedCheckoutSession.isLoading,
    isUpdatingCheckout:
      updateCheckoutSession.isLoading ||
      updateUnauthenticatedCheckoutSession.isLoading,
    isConfirmingCheckout: confirmCheckoutSession.isLoading,
    confirmCheckoutError: confirmCheckoutSession.error,
    checkoutError:
      startCheckoutSession.error ||
      updateCheckoutSession.error ||
      startUnauthenticatedCheckoutSession.error ||
      updateUnauthenticatedCheckoutSession.error ||
      confirmCheckoutSession.error,
    isCheckoutSuccess: confirmCheckoutSession.isSuccess,
    isCheckoutDisabled:
      shouldDisableCheckoutForError(startCheckoutSession.error) ||
      isCheckoutExpired,
    startCheckout,
    updateCheckout,
    confirmCheckout
  };
};

export default useMultiTierCheckoutSession;
